import { Injectable } from '@angular/core';
import { ToastInfo } from '../interfaces/toast-info';

@Injectable({ providedIn: 'root' })
export class ToastService {
  toasts: ToastInfo[] = [];

  private show(toast: ToastInfo) {
    this.toasts.push(toast);
  }

  showSuccess(message: string, header: string = 'Success', delayTime: number = 5000) {
    this.toasts.push({ header: header, body: message, delay: delayTime, class: 'text-success' });
  }

  showError(message: string, header: string = 'Error Occurred', delayTime: number = 5000) {
    this.toasts.push({ header: header, body: message, delay: delayTime, class: 'text-danger' });
  }

  /**
   * Report a FAILED HTTP call, showing what the server actually said.
   *
   * Exports (and anything else using `responseType: 'blob'`) get an HttpErrorResponse whose `error` is
   * a **Blob**, not JSON — so a handler that reads `err.error.message` finds nothing and falls back to
   * a network message. That is why a 500 on the export endpoint was reported for weeks as
   * "check your internet connection": the real error was there, just unreadable.
   *
   * Reading a Blob is async, so this returns void and shows the toast when the text arrives.
   */
  showServerError(error: any, fallback = 'Something went wrong. Please try again.') {
    const emit = (message: string) => this.showError(message || fallback);

    if (error?.error instanceof Blob) {
      error.error.text().then((text: string) => {
        try {
          const parsed = JSON.parse(text);
          emit(parsed?.message || parsed?.error || fallback);
        } catch {
          // A plain-text or HTML error page: show the first meaningful line rather than markup.
          const plain = (text || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
          emit(plain ? plain.slice(0, 300) : fallback);
        }
      }).catch(() => emit(fallback));
      return;
    }

    if (error?.status === 0) {
      emit('Could not reach the server. Please check your internet connection and try again.');
      return;
    }

    emit(error?.error?.message || error?.message || fallback);
  }

  remove(toast: ToastInfo) {
    this.toasts = this.toasts.filter((t) => t !== toast);
  }

  clear() {
    this.toasts.splice(0, this.toasts.length);
  }
}
